import React from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setActiveCourseData } from '@/lib/slices/course-slice';

import ConfirmationModal from '@/common/components/modals/general/ConfirmationModal';
import { DeleteIcon } from '@/assets/icons/button';

import Request from '@/services/util/request-service';
import { CourseData, UnitId } from 'course-types';

function DeleteUnitModal() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const activeUnitId: UnitId | undefined = useAppSelector(() => store.getState().unit.id);
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  // Helper function for data access
  const getActiveUnit = () => activeCourseData.units[activeUnitId as UnitId];

  const unitName = getActiveUnit()?.name ?? 'Undefined Unit';

  const handleDeleteUnit = async () => {
    try {
      const response = await (
        await Request.delete('course/removeUnit', { unitId: activeUnitId })
      )?.json();

      if (!response || response.error || !response.status) {
        throw new Error('Failed to delete unit');
      }

      // Helper function to remove unit from course data
      const removeUnitFromCourseData = (): CourseData => {
        const updatedUnits = { ...activeCourseData.units };
        delete updatedUnits[activeUnitId as UnitId];

        return {
          ...activeCourseData,
          units: updatedUnits,
        };
      };

      // Helper function to reindex remaining units
      const reindexUnits = (courseData: CourseData): void => {
        const sortedUnits = Object.entries(courseData.units)
          .map(([unitId, unitData]) => ({ unitId, ...unitData }))
          .sort((a, b) => a.index - b.index);

        sortedUnits.forEach((unit, newIndex) => {
          if (unit.index !== newIndex) {
            courseData.units[unit.unitId as UnitId] = {
              ...courseData.units[unit.unitId as UnitId],
              index: newIndex,
            };
          }
        });
      };

      const newCourseData = removeUnitFromCourseData();
      reindexUnits(newCourseData);

      dispatch(setActiveCourseData(newCourseData));
      toast.success('Unit deleted successfully');
    } catch (error) {
      console.error('Error deleting unit:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete unit');
      throw error;
    }
  };

  return (
    <ConfirmationModal
      modalName='DeleteUnit'
      title={`Delete "${unitName}"`}
      subtitle='This action cannot be undone'
      message='Are you sure you want to delete this unit?'
      description='All content within this unit will be permanently deleted. This includes all subunits, activities, and associated resources.'
      confirmText='Delete Unit'
      confirmIcon={<DeleteIcon className='w-5 h-5' />}
      isDestructive={true}
      onConfirm={handleDeleteUnit}
    />
  );
}

export default DeleteUnitModal;
